import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authClient } from "../../lib/authClient";

export function PasskeyAuth() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passkeyCapabilities, setPasskeyCapabilities] = useState(() => ({
    webAuthn: typeof window !== "undefined" && "PublicKeyCredential" in window,
    conditionalUI: false,
    checked: false,
  }));

  useEffect(() => {
    let isMounted = true;

    const detectCapabilities = async () => {
      const hasWindow = typeof window !== "undefined";
      const supportsWebAuthn = hasWindow && "PublicKeyCredential" in window;
      let conditionalAvailable = false;

      if (typeof navigator !== "undefined") {
        const container = navigator.credentials as
          | CredentialsContainer
          | undefined;
        const checkConditional = container?.isConditionalMediationAvailable;
        if (typeof checkConditional === "function") {
          try {
            conditionalAvailable = await checkConditional.call(container);
          } catch (capabilityError) {
            console.warn(
              "Falha ao detectar suporte a passkeys:",
              capabilityError,
            );
          }
        }
      }

      if (isMounted) {
        setPasskeyCapabilities({
          webAuthn: supportsWebAuthn,
          conditionalUI: conditionalAvailable,
          checked: true,
        });
      }
    };

    detectCapabilities();

    return () => {
      isMounted = false;
    };
  }, []);

  const handlePasskeySignIn = async (useAutoFill = false) => {
    setError(null);

    if (!passkeyCapabilities.webAuthn) {
      setError("Passkeys não são suportadas neste navegador/dispositivo.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Tenta fazer login com passkey
      const { error: signInError } = await authClient.signIn.passkey(
        useAutoFill ? { autoFill: true } : undefined,
      );

      if (signInError) {
        const normalizedMessage = signInError.message?.toLowerCase() ?? "";
        const errorCode = "code" in signInError ? signInError.code : undefined;
        if (errorCode === "AUTH_CANCELLED") {
          setError(
            "Solicitação cancelada. Tente novamente quando estiver pronto.",
          );
        } else if (
          errorCode === "PASSKEY_NOT_FOUND" ||
          normalizedMessage.includes("passkey not found")
        ) {
          setError(
            "Nenhuma passkey encontrada para este dispositivo. Entre por outro método e configure uma passkey na sua conta antes de usar este login.",
          );
        } else {
          setError(signInError.message ?? "Falha ao autenticar com passkey.");
        }
        return;
      }

      // Redireciona para o perfil após sucesso
      navigate("/profile", { replace: true });
    } catch (error) {
      console.error("Erro ao autenticar com passkey:", error);
      setError("Erro ao autenticar. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-method-content">
      <p className="auth-method-subtitle">
        Use biometria ou PIN do seu dispositivo para entrar de forma segura
      </p>

      <div className="passkey-info">
        <div className="passkey-icon">
          🔐
        </div>
        <div className="passkey-features">
          <div className="feature-item">
            <span className="feature-icon">✓</span>
            <span>Sem senhas necessárias</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">✓</span>
            <span>Autenticação biométrica</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">✓</span>
            <span>Mais seguro que senhas</span>
          </div>
        </div>
      </div>

      {!passkeyCapabilities.webAuthn && passkeyCapabilities.checked && (
        <p className="auth-error">
          Seu navegador não oferece suporte a passkeys. Atualize o dispositivo
          ou escolha outro método de login.
        </p>
      )}

      {error && <p className="auth-error">{error}</p>}

      <button
        type="button"
        onClick={() => handlePasskeySignIn()}
        className="primary-button"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Autenticando..." : "Entrar com Passkey"}
      </button>

      {passkeyCapabilities.conditionalUI && (
        <button
          type="button"
          className="secondary-button passkey-conditional-button"
          onClick={() => handlePasskeySignIn(true)}
          disabled={isSubmitting}
        >
          Usar sugestão do navegador
        </button>
      )}

      <p className="passkey-note">
        Você será solicitado a usar seu dispositivo (impressão digital, Face ID,
        PIN, etc.)
      </p>
    </div>
  );
}

declare global {
  interface CredentialsContainer {
    isConditionalMediationAvailable?: () => Promise<boolean>;
  }
}
